/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
/* eslint-disable no-nested-ternary */
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FormProvider, useFieldArray, useForm, useWatch
} from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { Label, Radio, Typography } from 'tfc-components';

import BillingInformation from './BillingInformation';
import ChoosePackage from './ChoosePackage';
import Confirmation from './Confirmation';
import PackageSelector from './PackageSelector';

import Icon from 'components/atoms/Icon';
import Loading from 'components/atoms/Loading';
import FooterCheckout from 'components/organisms/FooterCheckout';
import Section from 'components/organisms/Section';
import { addNewPackageRetryService, addNewPlanDiscountService, getOverViewService } from 'services/overview';
import { getMainPackagesService } from 'services/servicePackages';
import { ServicePackageAddOnData } from 'services/servicePackages/type';
import mapModifiers from 'utils/functions';

export interface PackageOption {
  id: number;
  name: string;
  amount: number;
  duration: number;
  description?: string;
  servicePackageAddOnData?: ServicePackageAddOnData[];
  numberOfAgent?: number;
  numberOfFile?: number;
  totalSizeLimit?: number;
  numberOfWebsite?: number;
  numberOfSession?: number;
  latestTier?: {
    numberOfAgent: number;
    numberOfFile: number;
    totalSizeLimit: number;
    numberOfWebsite: number;
    numberOfSession: number;
  };
}

export type InvoiceType = 'business' | 'individual';

export interface IBillingInfo {
  invoiceType: InvoiceType;
  invoice: IBusinessInfo | IIndividualInfo;
}

export interface IBusinessInfo {
  taxId: string;
  companyName: string;
  fullAddress: string;
  invoiceEmail?: string[];
}

export interface IIndividualInfo {
  nationalId: string;
  fullName: string;
  fullAddress: string;
  invoiceEmail?: string[];
}

export interface IPackage {
  id: string;
  name: string;
  duration: number;
  price: number;
  quantity: number;
  packageId?: string;
  latestTier?: {
    numberOfAgent: number;
    numberOfFile: number;
    totalSizeLimit: number;
    numberOfWebsite: number;
    numberOfSession: number;
  };
}

export interface PackageForm {
  packages: IPackage[];
}

const AddNewPackage: React.FC = () => {
  const [step, setStep] = useState(1);
  const [billingType, setBillingType] = useState<'one-time' | 'subscription'>('subscription');
  const billingFormSubmitRef = useRef<(() => void) | null>(null);
  const [billingInfoData, setBillingInfoData] = useState<IBillingInfo | null>(null);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);
  const [discountData, setDiscountData] = useState<{
    discountAmount: number;
    discountDay: number;
    servicePackageAddOns: {
      discountAmount?: number;
      maxQty?: number;
      quantity: number;
      servicePackageAddOnId: number;
      unit: number;
    }[];
  } | null>(null);

  const location = useLocation() as { state: { token?: string } };
  const tokenRetry = location.state?.token || '';
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  /* Form */
  const packageMethod = useForm<PackageForm>({
    defaultValues: {
      packages: [],
    },
    mode: 'all',
  });

  const { fields, append, remove } = useFieldArray({
    control: packageMethod.control,
    name: 'packages',
  });

  // theo dõi packages để tính total
  const packages = useWatch({
    control: packageMethod.control,
    name: 'packages',
  });

  const total = packages?.reduce((acc, pkg) => acc + pkg.price * (pkg.quantity || 1)
    * (billingType === 'subscription' ? 1 : pkg.duration || 1), 0) || 0;

  /* Queries */
  const { data: mainPackagesData, isLoading: isMainLoading } = useQuery({
    queryKey: ['getMainPackages'],
    queryFn: getMainPackagesService,
  });

  const { data: overViewData } = useQuery({
    queryKey: ['get-overview'],
    queryFn: getOverViewService,
  });

  /* Mutation */
  const { mutateAsync: getDiscount } = useMutation({
    mutationKey: ['add-new-discount'],
    mutationFn: addNewPlanDiscountService,
    onSuccess: (data) => {
      setDiscountData(data);
    },
  });

  const mainPackages: PackageOption[] = useMemo(() => {
    if (!mainPackagesData) return [];
    return mainPackagesData.data
      .map((pkg) => {
        if (!pkg.servicePackageData || typeof pkg.servicePackageData.id === 'undefined') {
          return {};
        }
        return {
          id: pkg.servicePackageData?.id || 0,
          name: pkg.servicePackageData.name || 'Unknown Package',
          duration: pkg.servicePackageData.duration || 0,
          amount: pkg.servicePackageData.price || 0,
          servicePackageAddOnData: pkg.servicePackageData.servicePackageAddOnData,
        };
      })
      .filter((pkg): pkg is PackageOption => pkg !== null);
  }, [mainPackagesData]);

  const mainPackagesByDuration = useMemo(
    () => mainPackages?.filter(
      (item) => item.duration === overViewData?.planData?.packageData?.duration,
    ),
    [mainPackages, overViewData?.planData?.packageData?.duration],
  );

  const { data: dataRetry } = useQuery({
    queryKey: ['addNewPackageRetry', tokenRetry],
    queryFn: () => addNewPackageRetryService(tokenRetry || ''),
    enabled: !!tokenRetry,
  });

  const handleSubmitDataStep1 = () => {
    setStep(2);
  };

  const handleSubmitDataStep2 = (data: IBillingInfo) => {
    setBillingInfoData(data);
    setStep(3);
  };

  const handleActionBack = () => {
    setStep(step - 1);
    setNextBtnDisabled(false);
  };

  const handleAction = () => {
    if (step === 1) {
      handleSubmitDataStep1();
    } else if (step === 2 && billingFormSubmitRef.current) {
      billingFormSubmitRef.current();
    }
  };

  function totalTiers() {
    const totals = {
      numberOfAgent: 0,
      numberOfFile: 0,
      numberOfSession: 0,
      numberOfWebsite: 0,
      totalSizeLimit: 0,
    };

    // Xử lý dữ liệu từ mảng: lặp qua từng phần tử trong mảng
    if (Array.isArray(packages)) {
      // Kiểm tra xem dataArray có phải là một mảng hợp lệ không
      packages.forEach((item) => {
        // Đảm bảo phần tử hiện tại và thuộc tính latestTier của nó tồn tại
        if (item && item.latestTier) {
          const tier = item.latestTier;
          // Cộng dồn các giá trị, sử dụng ||
          // 0 để xử lý trường hợp thiếu key hoặc giá trị null/undefined
          totals.numberOfAgent += tier.numberOfAgent * item.quantity || 0;
          totals.numberOfFile += tier.numberOfFile * item.quantity || 0;
          totals.numberOfSession += tier.numberOfSession * item.quantity || 0;
          totals.numberOfWebsite += tier.numberOfWebsite * item.quantity || 0;
          totals.totalSizeLimit += tier.totalSizeLimit * item.quantity || 0;
        }
      });
    }
    return totals;
  }

  const maxDuration = useMemo(() => {
    const arrDuration: number[] = packages.map((item) => item.duration);
    const max = Math.max(...arrDuration);
    return max;
  }, [packages]);

  useEffect(() => {
    getDiscount({
      servicePackageAddOns: packages.map((item) => ({
        quantity: item.duration,
        servicePackageAddOnId: parseInt(item?.packageId || '1', 10),
        unit: item?.quantity
      })),
    });
  }, [getDiscount, packages]);

  useEffect(() => {
    const addOnPackages = mainPackagesByDuration.find(
      (pkg) => pkg.id === overViewData?.planData?.packageData?.id
    )?.servicePackageAddOnData || [];

    if (dataRetry) {
      const mergedData = dataRetry?.servicePackageAddOns?.map((item) => {
        const fullInfo = addOnPackages.find((pkg) => pkg.id === item.servicePackageAddOnId);

        return {
          ...item,
          ...fullInfo
        };
      });

      packageMethod.reset({
        packages: mergedData.map((pck) => ({
          duration: pck.quantity || 0,
          id: pck.id !== undefined ? String(pck.id) : undefined,
          latestTier: {
            numberOfAgent: pck.numberOfAgent || 0,
            numberOfFile: pck.numberOfFile || 0,
            numberOfSession: pck.numberOfSession || 0,
            numberOfWebsite: pck?.numberOfWebsite || 0,
            totalSizeLimit: pck?.totalSizeLimit || 0
          },
          name: pck?.name || '',
          packageId: pck.id !== undefined ? String(pck.id) : undefined,
          price: pck?.price || 0,
          quantity: pck?.unit || 0,

        }))
      });

      setBillingType(dataRetry?.billingType === 1 ? 'one-time' : 'subscription');
      setBillingInfoData({
        invoiceType: dataRetry?.invoiceData?.invoiceType === 1 ? 'individual' : 'business',
        invoice: dataRetry?.invoiceData?.invoiceType === 1 ? {
          fullAddress: dataRetry?.invoiceData?.fullAddress,
          fullName: dataRetry?.invoiceData?.companyName,
          nationalId: dataRetry?.invoiceData?.taxCode,
          invoiceEmail: dataRetry?.invoiceData?.invoiceEmails
        } : {
          companyName: dataRetry?.invoiceData?.companyName,
          taxId: dataRetry?.invoiceData?.taxCode,
          fullAddress: dataRetry?.invoiceData?.fullAddress,
          invoiceEmail: dataRetry?.invoiceData?.invoiceEmails
        }
      });
      setStep(3);
    } else {
      setBillingType(overViewData?.planData?.billingType === 2 ? 'subscription' : 'one-time');
      setBillingInfoData({
        invoice: {
          invoiceEmail: overViewData?.invoiceData?.invoiceEmails,
          taxId: overViewData?.invoiceData?.taxCode || '',
          companyName: overViewData?.invoiceData?.companyName || '',
          fullAddress: overViewData?.invoiceData?.fullAddress || '',
          nationalId: overViewData?.invoiceData?.nationalId || '',
          fullName: overViewData?.invoiceData?.fullName || '',
        },
        invoiceType: overViewData?.invoiceData.invoiceType === 2 ? 'business' : 'individual',
      });
    }
  }, [dataRetry, mainPackagesByDuration, overViewData?.invoiceData?.companyName,
    overViewData?.invoiceData?.fullAddress, overViewData?.invoiceData?.fullName,
    overViewData?.invoiceData?.invoiceEmails, overViewData?.invoiceData.invoiceType,
    overViewData?.invoiceData?.nationalId, overViewData?.invoiceData?.taxCode,
    overViewData?.planData?.billingType, overViewData?.planData?.packageData?.id, packageMethod]);

  return isMainLoading ? (
    <div className="p-checkout_paymentStatus_loading">
      <Loading />
    </div>
  ) : (
    <>
      <Section>
        <div className="p-checkout">
          <div className="p-checkout_steps">
            {[1, 2, 3].map((val) => (
              <React.Fragment key={val}>
                <div
                  className={mapModifiers('p-checkout_steps_step', step === val ? 'active' : '')}
                >
                  <div
                    className={mapModifiers('p-checkout_steps_number', step > val ? 'active' : '')}
                  >
                    {step > val ? (
                      <Icon iconName="checkWhite" size="20" />
                    ) : (
                      <Typography.Text extendClasses="p-checkout_steps_numberText fs-20x24">
                        {val}
                      </Typography.Text>
                    )}
                  </div>
                  <Typography.Text extendClasses="p-checkout_steps_title fs-20x24">
                    {val === 1
                      ? 'Select Package'
                      : val === 2
                        ? 'Billing Information'
                        : 'Confirmation'}
                  </Typography.Text>
                </div>
                {val < 3 && <div className="p-checkout_steps_divider" />}
              </React.Fragment>
            ))}
          </div>

          <div className="p-checkout_content">
            {step === 1 && (
              <>
                <FormProvider {...packageMethod}>
                  <ChoosePackage
                    billingType={billingType}
                    total={total - (discountData?.discountAmount || 0)}
                    duration={
                      overViewData?.planData?.packageData?.duration === 3 ? 'yearly' : 'monthly'
                    }
                    visiblePackages={packages}
                    method={packageMethod}
                    packageQty={overViewData?.planData?.packageQty || 1}
                    discountData={discountData}
                  />
                </FormProvider>

                <div className="p-checkout_billingType p-addNewPackage_mt32">
                  <Typography.Text
                    fontweight="600"
                    extendClasses="p-checkout_titleProduct fs-20x24"
                  >
                    Billing type
                  </Typography.Text>
                  <div className="p-checkout_billingType_options">
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        checked={billingType === 'one-time'}
                        onChange={() => setBillingType('one-time')}
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">
                        One-time purchase
                      </Typography.Text>
                    </Label>
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        style={{ backgroundColor: 'red' }}
                        checked={billingType === 'subscription'}
                        onChange={() => setBillingType('subscription')}
                        disabled={overViewData?.planData?.billingType === 1}
                        readOnly={overViewData?.planData?.billingType === 1}
                        extendClasses={
                          overViewData?.planData?.billingType === 1
                            ? 'p-addNewPackage_radioDisable'
                            : ''
                        }
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">
                        Subscription
                      </Typography.Text>
                    </Label>
                  </div>
                </div>
                <PackageSelector
                  currentMainPackage={
                    overViewData?.planData?.packageData || {
                      id: 37,
                      name: 'Starter',
                      duration: 3,
                      price: 100000,
                    }
                  }
                  mainPackages={mainPackagesByDuration || []}
                  isAddonPackageLoading={isMainLoading}
                  selectedAddons={packages}
                  duration={
                    overViewData?.planData?.packageData?.duration === 3 ? 'yearly' : 'monthly'
                  }
                  selectPackage={(data) => {
                    const foundIndex = fields.findIndex((f) => f.packageId === data.id);
                    if (foundIndex === -1) {
                      append({ ...data, packageId: data?.id });
                    } else {
                      packageMethod.setValue('packages', packages.filter((item) => item?.packageId !== data?.id));
                    }
                  }}
                />
              </>
            )}
            {step === 2 && (
              <BillingInformation
                onSubmit={handleSubmitDataStep2}
                triggerSubmitRef={billingFormSubmitRef}
                initialData={billingInfoData || undefined}
              />
            )}
            {step === 3 && (
              <Confirmation
                billingType={billingType}
                billingData={billingInfoData!}
                total={total - (discountData?.discountAmount || 0)}
                duration={
                  overViewData?.planData?.packageData?.duration === 3 ? 'yearly' : 'monthly'
                }
                visiblePackages={packages}
                method={packageMethod}
                discountData={discountData}
              />
            )}
          </div>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6Lcp4MIZAAAAABHdGwULZhEv01tu2iODEDzSterA"
            size="invisible"
          />
        </div>
      </Section>
      <FooterCheckout
        step={step}
        actionBack={() => handleActionBack()}
        action={handleAction}
        disabled={nextBtnDisabled || fields.length <= 0}
      />
    </>
  );
};

export default AddNewPackage;
